import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { cget, cset } from "@/lib/cached";

export default function StaticPage({ pageKey }) {
  const [copy, setCopy] = useState(() => cget("imu_copy") || {});
  const { user } = useAuth();
  const nav = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    api.get("/settings/copy").then((r) => { setCopy(r.data); cset("imu_copy", r.data); }).catch(() => {});
  }, []);
  const t = (k, f = "") => copy[k] ?? f;

  if (pageKey === "about") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24" data-testid="about-page">
        <div className="eyebrow">{t("about_subtitle", "Science · Nature · You")}</div>
        <h1 className="mt-3 font-display text-4xl sm:text-6xl text-ink-900">{t("about_title", "About IMMUSKI")}</h1>
        <p className="mt-8 text-ink-700 leading-relaxed whitespace-pre-line">{t("about_body", "")}</p>
        <div className="mt-12 border-t hairline pt-8">
          <div className="eyebrow">{t("about_mission_title", "Our mission")}</div>
          <p className="mt-3 font-display text-2xl text-ink-900">{t("about_mission_body", "")}</p>
        </div>
      </div>
    );
  }

  if (pageKey === "contact") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24" data-testid="contact-page">
        <div className="eyebrow">Care</div>
        <h1 className="mt-3 font-display text-4xl sm:text-6xl text-ink-900">{t("contact_title", "Contact Us")}</h1>
        <p className="mt-3 text-ink-500">{t("contact_subtitle", "")}</p>
        <p className="mt-6 text-ink-700 leading-relaxed">{t("contact_support_line", "")}</p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Email"><a className="link-underline text-ink-900" href={`mailto:${t("contact_email")}`}>{t("contact_email")}</a></Field>
          <Field label="Phone"><a className="link-underline text-ink-900" href={`tel:${t("contact_phone").replace(/\s+/g, "")}`}>{t("contact_phone")}</a></Field>
          <Field label="WhatsApp"><a className="link-underline text-ink-900" target="_blank" rel="noopener noreferrer" href={`https://wa.me/${t("contact_whatsapp").replace(/[^0-9]/g, "")}`}>{t("contact_whatsapp")}</a></Field>
          <Field label="Hours">{t("contact_hours")}</Field>
          <div className="sm:col-span-2"><Field label="Address">{t("contact_address")}</Field></div>
        </div>
      </div>
    );
  }

  if (pageKey === "derm") {
    const bookConsult = async () => {
      if (!user) {
        toast.info("Please sign in first to continue seamlessly");
        nav("/auth?next=/dermatologists");
        return;
      }
      setRedirecting(true);
      try {
        const { data } = await api.get("/auth/sso/1clin");
        window.location.href = data.redirect_url;
      } catch {
        toast.error("Could not open the consult portal");
        setRedirecting(false);
      }
    };
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24" data-testid="derm-page">
        <div className="eyebrow">Personalised care</div>
        <h1 className="mt-3 font-display text-4xl sm:text-6xl text-ink-900">{t("derm_title", "Talk to a Dermatologist")}</h1>
        <p className="mt-3 text-ink-500">{t("derm_subtitle", "")}</p>
        <p className="mt-8 text-ink-700 leading-relaxed whitespace-pre-line">{t("derm_body", "")}</p>
        <div className="mt-6 border hairline p-5 bg-white">
          <div className="eyebrow">How it works</div>
          <ol className="mt-3 space-y-2 text-sm text-ink-700 list-decimal list-inside">
            <li>Click below — we securely sign you into our partner consult portal <span className="font-medium">1clin</span>.</li>
            <li>You do not need to sign up again — your IMMUSKI account carries over.</li>
            <li>Book a video slot, meet the doctor, receive your prescription — all inside 1clin.</li>
          </ol>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <button onClick={bookConsult} disabled={redirecting}
            className="btn-primary-ink px-8 py-4 text-xs uppercase tracking-[0.28em] disabled:opacity-50"
            data-testid="derm-book-btn">
            {redirecting ? "Opening portal…" : (user ? "Continue to 1clin →" : "Sign in to book a consult")}
          </button>
          <a href={`mailto:${t("contact_email")}`} className="btn-gold-outline px-8 py-4 text-xs uppercase tracking-[0.28em]">
            Or email us
          </a>
        </div>
      </div>
    );
  }
  if (pageKey === "returns") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24" data-testid="returns-page">
        <div className="eyebrow">{t("returns_subtitle", "Care & Policies")}</div>
        <h1 className="mt-3 font-display text-4xl sm:text-6xl text-ink-900">{t("returns_title", "Return & Refund Policy")}</h1>
        <p className="mt-6 text-ink-700 leading-relaxed whitespace-pre-line">
          {t("returns_body", "")}
        </p>
      </div>
    );
  }

  return null;
}

const Field = ({ label, children }) => (
  <div className="border hairline p-4 bg-white">
    <div className="eyebrow">{label}</div>
    <div className="mt-2 text-sm text-ink-900">{children}</div>
  </div>
);
